'use client'

import React, { FC, ReactElement } from 'react'
import classNames from 'classnames'
import Link from 'next/link'
import styles from './Wallet.module.css'
import WalletBalance from './wallet-balance'
import WalletReceive from './wallet-receive'
import WalletSend from './wallet-send'
import WalletSign from './wallet-sign'
import WalletItemFeed from './wallet-item-feed'
import WalletHistory from './wallet-history'
import WalletStatus from './wallet-confirmed'
import WalletUtxos from './wallet-utxos'
import CollectionContainedDropdownSelect from '@/components/collection-contained-dropdown-select'
import LineDivider from '@/components/line-divider'
import useWalletWarning from '@/utils/hooks/useWalletWarning'
import useWalletBalance from '@/utils/hooks/useWalletBalance'
import useMoonpayModal from '@/utils/hooks/useMoonpayModal'
import event from '@/utils/event'
import WalletDescription from './wallet-description'
import Modal from '@/components/modal-v2'
import Tabs from '@/components/tabs'
import { NAMES_SLUGS } from '@/utils/constants'
import SatoshiContext from '@/components/satoshis/context'
import ButtonOutlinedGradient from '@/components/button-outlined-gradient-epic'
import LocalEscrowsContext from '@/components/local-escrows/context'
import PlusCircleIcon from '@/icons/plus-circle'
import UtxosCart from './wallet-utxos/utxos-cart'
import { FILTER_COLLECTIONS_DEFAULT_ID, EXPLORER_URL } from '@/utils/constants'
import Wallet from '@/utils/wallet'
import featureFlags from '@/utils/feature-flags'

type Tab = 'my-inscriptions' | 'wallet-history' | 'brc20' | 'sns' | 'utxos'
const DEFAULT = 0
const RECEIVE = 1
const SEND = 2
const SIGN = 3
const MANAGE_UTXOS = 4

const WALLET_TABS_ID = 'wallet-tabs'

const WalletPage: FC<{ isMobile: boolean }> = (props): ReactElement => {
	const [open, setOpen] = React.useState(false)
	const tickerRef = React.useRef('')
	const [walletAction, setWalletAction] = React.useState(DEFAULT)

	// Ensure page starts at top on mount
	React.useEffect(() => {
		window.scrollTo(0, 0)
	}, [])
	const [openModal] = useMoonpayModal()
	const [selectedCollection, setSelectedCollection] = React.useState<string>(
		FILTER_COLLECTIONS_DEFAULT_ID
	)
	const [selectedCollectionSlug, setSelectedCollectionSlug] = React.useState<string>('')

	const handleManageUtxos = React.useCallback(() => {
		setOpen(true)
		setWalletAction(walletAction !== MANAGE_UTXOS ? MANAGE_UTXOS : DEFAULT)
	}, [walletAction])

	const toggleReceive = React.useCallback(() => {
		setOpen(true)
		setWalletAction(walletAction !== RECEIVE ? RECEIVE : DEFAULT)
	}, [walletAction])

	const toggleSend = React.useCallback(() => {
		setOpen(true)
		setWalletAction(walletAction !== SEND ? SEND : DEFAULT)
	}, [walletAction])

	const toggleSign = React.useCallback(() => {
		setOpen(true)
		setWalletAction(walletAction !== SIGN ? SIGN : DEFAULT)
	}, [walletAction])

	useWalletWarning()
	const { data: data } = useWalletBalance()

	const [tab, setTab] = React.useState<Tab>('my-inscriptions')
	const tabs = React.useMemo(
		() =>
			[
				{
					title: 'NFT',
					onClick: () => setTab('my-inscriptions'),
					selected: tab === 'my-inscriptions'
				},
				!featureFlags('app::disable_brc20') && {
					title: 'Tokens',
					onClick: () => setTab('brc20'),
					selected: tab === 'brc20'
				},
				!featureFlags('app::disable_sns') && {
					title: 'SNS',
					onClick: () => setTab('sns'),
					selected: tab === 'sns'
				},
				{
					title: 'UTXOS',
					onClick: () => setTab('utxos'),
					selected: tab === 'utxos'
				},
				{
					title: 'History',
					onClick: () => {
						window.open(`${EXPLORER_URL}/address/${Wallet.getTaprootAddress()}`, '_blank')
						//setTab('wallet-history')
					},
					selected: tab === 'wallet-history',
					status: data?.unconfirmed_balance > 0 && (
						<WalletStatus status={'unconfirmed'} className={styles.walletHistoryStatus} />
					)
				}
			].filter((e) => e),
		[tab, data]
	)

	const INITIAL_FILTER = React.useMemo(
		() => ({
			title: 'Filter Collections',
			id: FILTER_COLLECTIONS_DEFAULT_ID,
			onClick: () => {
				setSelectedCollection(FILTER_COLLECTIONS_DEFAULT_ID)
				setSelectedCollectionSlug('')
			}
		}),
		[]
	)
	const inscriptions = data?.inscriptions

	const filters = React.useMemo(() => {
		let _filters: any[] = []
		if (inscriptions?.length > 0) {
			// Build a Set of distinct slugs O(1) lookups + a count map in one pass.
			const seenSlugs = new Set<string>()
			const namesSlugSet = new Set(NAMES_SLUGS)
			const countBySlug = new Map<string, number>()
			for (const ins of inscriptions) {
				const slug = ins?.collection?.slug
				if (slug) countBySlug.set(slug, (countBySlug.get(slug) || 0) + 1)
			}
			for (var element of inscriptions) {
				const slug = element?.collection?.slug
				const collectionName = element?.collection?.name
				if (collectionName && slug && !seenSlugs.has(slug)) {
					seenSlugs.add(slug)
					const count = countBySlug.get(slug) || 0
					const filterTitle = count > 1 ? `${collectionName} (${count})` : collectionName
					const _filter: any = {
						title: filterTitle,
						id: element?.id,
						collection: element?.collection,
						slug,
						count
					}
					if (!namesSlugSet.has(slug)) {
						_filters.push(_filter)
					}
				}
			}
		}
		if (_filters?.length > 1) {
			_filters = _filters.sort((a, b) => (a.title < b.title ? -1 : a.title > b.title ? 1 : 0))
		}

		_filters.unshift(INITIAL_FILTER)

		return _filters
	}, [INITIAL_FILTER, inscriptions])

	React.useEffect(() => {
		const handleSuccess = (payload: any) => {
			if (payload.type === 'send' && payload.event === 'success') {
				setWalletAction(DEFAULT)
			}
		}

		event.listen('open-broadcast-modal', handleSuccess)

		return () => {
			event.removeListener('open-broadcast-modal', handleSuccess)
		}
	}, [])

	const handleCloseModal = () => {
		setOpen(false)
		setWalletAction(DEFAULT)
	}

	const openTab = (tab: Tab, ticker?: string) => {
		;(document as any).getElementById(WALLET_TABS_ID).scrollIntoView({
			behavior: 'smooth'
		})
		tickerRef.current = ticker as any
		setTab(tab)
	}

	return (
		<SatoshiContext>
			<LocalEscrowsContext>
				<div className={styles.root}>
					<div className={styles.backgroundGradientContainer}>
						<div className={styles.backgroundGradient} />
						<div className={styles.walletHeader}>
							<div>
								<WalletBalance
									toggleSignMessage={toggleSign}
									toggleSendCoin={toggleSend}
									toggleRecieveCoin={toggleReceive}
									toggleTopupModal={openModal}
								/>
							</div>
							<WalletDescription openTab={openTab} activeOffers={0} />
						</div>
					</div>
					<LineDivider className={styles.line} />
					<div className={styles.tabSection} id={WALLET_TABS_ID}>
						<div className={classNames(styles.title, styles.desktopOnly)}>
							{tab === 'utxos' ? 'UTXOs' : 'Items'}
							{tab === 'my-inscriptions' && (
								<CollectionContainedDropdownSelect
									options={filters}
									selected={selectedCollection}
									setSelected={(filter: string, slug: string) => {
										setSelectedCollection(filter)
										setSelectedCollectionSlug(slug)
									}}
								/>
							)}
							{tab === 'utxos' && (
								<ButtonOutlinedGradient
									className={styles.titleActionButton}
									size="small"
									onClick={handleManageUtxos}
								>
									<PlusCircleIcon className={styles.titleActionIcon} /> Manage UTXOs
								</ButtonOutlinedGradient>
							)}
						</div>
						<Tabs className={styles.tabs} tabs={tabs as any} />
						{tab === 'utxos' && (
							<ButtonOutlinedGradient
								className={classNames(styles.titleActionButton, styles.mobileOnly)}
								size="small"
								onClick={handleManageUtxos}
							>
								<PlusCircleIcon className={styles.titleActionIcon} /> Manage UTXOs
							</ButtonOutlinedGradient>
						)}
					</div>
					{tab === 'wallet-history' && <WalletHistory />}
					{tab === 'my-inscriptions' && (
						<WalletItemFeed
							subTab={tab}
							nameSlugs={NAMES_SLUGS}
							isMobile={props.isMobile}
							selectedCollectionFilter={selectedCollection}
							selectedCollectionSlug={selectedCollectionSlug}
							setSelected={(filter: string, slug: string) => {
								setSelectedCollection(filter)
								setSelectedCollectionSlug(slug)
							}}
						/>
					)}
					{tab === 'brc20' && (
						<WalletItemFeed subTab={tab} nameSlugs={NAMES_SLUGS} isMobile={props.isMobile} />
					)}
					{tab === 'sns' ? (
						<WalletItemFeed subTab={tab} nameSlugs={NAMES_SLUGS} isMobile={props.isMobile} />
					) : (
						[]
					)}
					{tab === 'utxos' && <WalletUtxos />}

					{/* Link to new wallet */}
					<div className={styles.newWalletLink}>
						<Link href="/wallet">Switch to new wallet</Link>
					</div>
				</div>
				<Modal open={open} onClose={handleCloseModal} bodyClassName={styles.modal}>
					{walletAction === SEND && <WalletSend handleModalClose={handleCloseModal} />}
					{walletAction === RECEIVE && <WalletReceive handleModalClose={handleCloseModal} />}
					{walletAction === SIGN && <WalletSign handleModalClose={handleCloseModal} />}
					{walletAction === MANAGE_UTXOS && <UtxosCart handleModalClose={handleCloseModal} />}
				</Modal>
			</LocalEscrowsContext>
		</SatoshiContext>
	)
}

export default WalletPage
